/**
 * L5: the Face ID seam (01-references §3: WebAuthn behind a tap; 03 §F-2: a WebAuthn-PRF key
 * later releases the replica key).
 *
 * J-3704 ships a PLACEHOLDER behind the real `Unlocker` interface. It calls no WebAuthn API and
 * simply resolves after a short, visible "waiting for Face ID" beat, so the lock-screen states
 * can be reviewed. It never falls back to a password or to a confirm dialog (03 SI-12, fail closed).
 * J-3705 replaces it with the WebAuthn-PRF unlocker; no screen changes.
 *
 * FIXTURE BUILD ONLY (CSO review of PR #95). Only src/entry/fixture.ts imports this module, the
 * shell refuses to start it in front of a non-fixture data source, and the bundle-safety test
 * fails if any non-fixture build contains it. It has no default outcome: an outcome other than
 * "ok" answers not-ok, so a caller that forgets to choose one stays locked.
 */
import type { Unlocker, UnlockResult } from "../contract.js";
import { issueSession } from "./session.js";

export type PlaceholderOutcome = "ok" | "cancelled" | "unavailable";

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class PlaceholderUnlocker implements Unlocker {
  readonly method = "placeholder" as const;

  constructor(
    private readonly outcome: PlaceholderOutcome,
    private readonly delayMs = 700,
    private readonly sleep: (ms: number) => Promise<void> = wait,
  ) {}

  /** There is no real request to cancel, so the abort signal is ignored; the shell discards a stale answer. */
  async unlock(_signal?: AbortSignal): Promise<UnlockResult> {
    await this.sleep(this.delayMs);
    if (this.outcome === "ok") return { ok: true, session: issueSession("placeholder") };
    return { ok: false, reason: this.outcome === "unavailable" ? "unavailable" : "cancelled" };
  }
}

/**
 * Review hook, fixture builds only: `?unlock=cancel` or `?unlock=unavailable` shows those
 * lock-screen states. Anything else unlocks, because behind this unlocker there is only fake data.
 */
export function unlockerFromSearch(search: string, sleep?: (ms: number) => Promise<void>): PlaceholderUnlocker {
  const v = new URLSearchParams(search).get("unlock");
  const outcome: PlaceholderOutcome = v === "cancel" ? "cancelled" : v === "unavailable" ? "unavailable" : "ok";
  return new PlaceholderUnlocker(outcome, 700, sleep);
}
